/**
 * Bridge to the native background setup runner. Denker runs the allowlisted
 * install, sign-in or update command as a hidden child process; raw CLI
 * output never leaves Rust, so the webview only sees coarse progress and a
 * classified outcome.
 */
import { invoke } from "@tauri-apps/api/core";
import { listen } from "@tauri-apps/api/event";
import type { CliInstallInfo } from "@/lib/cli-install-commands";
import { safeTauriUnlisten } from "@/desktop/tauri-listener-cleanup";
import type { RuntimeSetupRequest } from "./runtime-setup-status";

const RUN_EVENT = "denker://runtime-setup-run";

export type RuntimeSetupRunReason =
  | "network"
  | "permission"
  | "port_in_use"
  | "installer_busy"
  | "unknown";

export type RuntimeSetupRunEvent =
  | {
      type: "progress";
      run_id: string;
      state: "running" | "awaiting_browser";
      sign_in_page: boolean;
    }
  | {
      type: "finished";
      run_id: string;
      outcome: "succeeded" | "failed" | "timed_out" | "cancelled";
      reason: RuntimeSetupRunReason | null;
      exit_code: number | null;
    };

/** Native allowlist check: only known install locations may self-update. */
export function runtimeUpdateSupported(
  choice: CliInstallInfo,
  executablePath: string,
): Promise<boolean> {
  return invoke<boolean>("runtime_setup_update_supported", {
    runtime: choice.id,
    executablePath,
  }).catch(() => false);
}

/** Rejects when native code cannot resolve or spawn the command. */
export function startRuntimeSetupRun(
  runId: string,
  request: RuntimeSetupRequest,
): Promise<void> {
  return invoke("runtime_setup_run_start", {
    request: {
      run_id: runId,
      runtime: request.choice.id,
      action: request.kind,
      executable_path: request.executablePath ?? null,
    },
    ...(request.loginId
      ? {
          loginId: request.loginId,
          signInWindowTitle: request.signInWindowTitle ?? null,
        }
      : {}),
  });
}

export function cancelRuntimeSetupRun(runId: string): Promise<void> {
  return invoke("runtime_setup_run_cancel", { runId });
}

export function cancelAllRuntimeSetupRuns(): Promise<void> {
  return invoke("runtime_setup_run_cancel_all");
}

/**
 * Opens the run's sign-in page in the user's browser (for an added login,
 * instead of its sign-in window); the URL stays in Rust.
 */
export function openRuntimeSetupSignInPage(runId: string): Promise<void> {
  return invoke("runtime_setup_run_open_sign_in", { runId });
}

/**
 * Resolves once the listener is registered, so a run started afterwards
 * never misses its first event. The cleanup silences the handler at once,
 * even if Tauri installs the webview side of the listener late.
 */
export async function watchRuntimeSetupRun(
  runId: string,
  onEvent: (event: RuntimeSetupRunEvent) => void,
): Promise<() => void> {
  let active = true;
  const unlisten = await listen<RuntimeSetupRunEvent>(RUN_EVENT, (event) => {
    if (active && event.payload.run_id === runId) onEvent(event.payload);
  });
  return () => {
    active = false;
    safeTauriUnlisten(unlisten);
  };
}
